import { useState, useEffect } from 'react';
import { Check, X, User } from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Image } from '@/components/ui/image';
import { SPORT_AVATARS, DEFAULT_AVATAR } from '@/data/sport-avatars';

export interface ProfileEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentName: string;
  currentAvatar?: string;
  onSave: (data: { name: string; avatar: string }) => void;
}

export default function ProfileEditDialog({
  open,
  onOpenChange,
  currentName,
  currentAvatar,
  onSave,
}: ProfileEditDialogProps) {
  const [name, setName] = useState(currentName || '学员');
  const [selectedAvatar, setSelectedAvatar] = useState(currentAvatar || DEFAULT_AVATAR);

  useEffect(() => {
    if (open) {
      setName(currentName || '学员');
      setSelectedAvatar(currentAvatar || DEFAULT_AVATAR);
    }
  }, [open, currentName, currentAvatar]);

  const handleSave = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error('昵称不能为空');
      return;
    }
    if (trimmedName.length > 12) {
      toast.error('昵称最多 12 个字');
      return;
    }
    onSave({ name: trimmedName, avatar: selectedAvatar });
    onOpenChange(false);
    toast.success('个人资料已更新');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm rounded-2xl p-0">
        <DialogHeader className="px-5 pt-5 pb-0">
          <DialogTitle className="text-lg font-bold">编辑资料</DialogTitle>
        </DialogHeader>

        <div className="px-5 py-4 space-y-5">
          {/* 当前头像预览 + 昵称输入 */}
          <div className="flex items-center gap-4">
            <div className="relative shrink-0">
              <Image
                src={selectedAvatar}
                alt="当前头像"
                className="w-16 h-16 rounded-full border-2 border-white shadow-md object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <label className="text-xs text-muted-foreground mb-1.5 block">昵称</label>
              <div className="relative">
                <User className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="请输入昵称"
                  maxLength={12}
                  className="pl-9 h-10"
                />
              </div>
            </div>
          </div>

          {/* 头像选择网格 */}
          <div>
            <p className="text-xs text-muted-foreground mb-3">选择头像</p>
            <div className="grid grid-cols-4 gap-3">
              {SPORT_AVATARS.map((avatar) => {
                const isSelected = selectedAvatar === avatar.url;
                return (
                  <button
                    key={avatar.id}
                    type="button"
                    onClick={() => setSelectedAvatar(avatar.url)}
                    className={`relative aspect-square rounded-xl overflow-hidden transition-all ${
                      isSelected
                        ? 'ring-2 ring-primary ring-offset-2 scale-105'
                        : 'ring-1 ring-border/50 hover:ring-primary/50'
                    }`}
                  >
                    <Image
                      src={avatar.url}
                      alt={avatar.label}
                      className="w-full h-full object-cover"
                    />
                    {isSelected && (
                      <div className="absolute top-1 right-1 size-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-sm">
                        <Check className="size-3" />
                      </div>
                    )}
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/50 to-transparent pt-3 pb-1">
                      <span className="text-[10px] text-white font-medium block text-center">
                        {avatar.label}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* 底部操作 */}
        <div className="flex gap-3 px-5 pb-5 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1 h-11"
          >
            取消
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            className="flex-1 h-11"
          >
            保存
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
